/**
 * CSS custom properties for the Figma token chain.
 * Brand variables hold raw values. Alias and mapped variables use var().
 */

import { brandColors, brandSpace } from './brand';
import { phoneTypeSpecs } from './phoneTypography';
import { fontFamily } from './typography';

export const brandColorVariables = {
  '--yovo-figma-neutrals-base-white': brandColors.neutrals.baseWhite,
  '--yovo-figma-neutrals-grey-100': brandColors.neutrals.grey100,
  '--yovo-figma-neutrals-grey-200': brandColors.neutrals.grey200,
  '--yovo-figma-neutrals-grey-300': brandColors.neutrals.grey300,
  '--yovo-figma-neutrals-grey-400': brandColors.neutrals.grey400,
  '--yovo-figma-neutrals-grey-500': brandColors.neutrals.grey500,
  '--yovo-figma-neutrals-grey-600': brandColors.neutrals.grey600,
  '--yovo-figma-neutrals-grey-700': brandColors.neutrals.grey700,
  '--yovo-figma-neutrals-grey-800': brandColors.neutrals.grey800,
  '--yovo-figma-neutrals-grey-900': brandColors.neutrals.grey900,
  '--yovo-figma-neutrals-base-1000': brandColors.neutrals.base1000,
  '--yovo-figma-neutrals-base-black': brandColors.neutrals.baseBlack,
  '--yovo-figma-primary-yovo-red-100': brandColors.primary.yovoRed100,
  '--yovo-figma-primary-yovo-red-200': brandColors.primary.yovoRed200,
  '--yovo-figma-primary-yovo-red-300': brandColors.primary.yovoRed300,
  '--yovo-figma-primary-yovo-red-400': brandColors.primary.yovoRed400,
  '--yovo-figma-primary-yovo-red-500': brandColors.primary.yovoRed500,
  '--yovo-figma-secondary-secondary-100': brandColors.secondary.secondary100,
  '--yovo-figma-secondary-secondary-200': brandColors.secondary.secondary200,
  '--yovo-figma-secondary-secondary-300': brandColors.secondary.secondary300,
  '--yovo-figma-secondary-secondary-400': brandColors.secondary.secondary400,
  '--yovo-figma-secondary-secondary-500': brandColors.secondary.secondary500,
  '--yovo-figma-tertiary-tertiary-50': brandColors.tertiary.tertiary50,
  '--yovo-figma-tertiary-tertiary-200': brandColors.tertiary.tertiary200,
  '--yovo-figma-tertiary-secondary-200': brandColors.tertiary.secondary200,
  '--yovo-figma-tertiary-secondary-400': brandColors.tertiary.secondary400,
  '--yovo-figma-accents-accents-100': brandColors.accents.accents100,
  '--yovo-figma-accents-accents-200': brandColors.accents.accents200,
  '--yovo-figma-accents-accents-300': brandColors.accents.accents300,
  '--yovo-figma-semantic-red-100': brandColors.semantic.red100,
  '--yovo-figma-semantic-red-200': brandColors.semantic.red200,
  '--yovo-figma-semantic-red-300': brandColors.semantic.red300,
  '--yovo-figma-semantic-red-400': brandColors.semantic.red400,
  '--yovo-figma-semantic-green-100': brandColors.semantic.green100,
  '--yovo-figma-semantic-green-200': brandColors.semantic.green200,
  '--yovo-figma-semantic-green-300': brandColors.semantic.green300,
  '--yovo-figma-semantic-green-400': brandColors.semantic.green400,
  '--yovo-figma-semantic-yellow-100': brandColors.semantic.yellow100,
  '--yovo-figma-semantic-yellow-200': brandColors.semantic.yellow200,
  '--yovo-figma-semantic-yellow-300': brandColors.semantic.yellow300,
  '--yovo-figma-semantic-yellow-400': brandColors.semantic.yellow400,
};

export const brandSpaceVariables = {
  '--yovo-figma-space-none': `${brandSpace.none}px`,
  '--yovo-figma-space-xxs': `${brandSpace.xxs}px`,
  '--yovo-figma-space-xs': `${brandSpace.xs}px`,
  '--yovo-figma-space-s': `${brandSpace.s}px`,
  '--yovo-figma-space-m': `${brandSpace.m}px`,
  '--yovo-figma-space-l': `${brandSpace.l}px`,
  '--yovo-figma-space-xl': `${brandSpace.xl}px`,
  '--yovo-figma-space-xxl': `${brandSpace.xxl}px`,
  '--yovo-figma-space-xxxl': `${brandSpace.xxxl}px`,
  '--yovo-figma-space-4xl': `${brandSpace.xl4}px`,
  '--yovo-figma-space-5xl': `${brandSpace.xl5}px`,
  '--yovo-figma-space-small-gaps': `${brandSpace.smallGaps}px`,
  '--yovo-figma-space-gaps': `${brandSpace.gaps}px`,
  '--yovo-figma-space-large-gaps': `${brandSpace.largeGaps}px`,
  '--yovo-figma-space-curve': `${brandSpace.curve}px`,
};

/** Alias color variables. The value is a brand variable, not a second hex. */
export const aliasColorLinks = [
  ['--yovo-figma-background-bg-01', '--yovo-figma-neutrals-base-white'],
  ['--yovo-figma-background-bg-02', '--yovo-figma-neutrals-grey-100'],
  ['--yovo-figma-background-bg-03', '--yovo-figma-neutrals-grey-200'],
  ['--yovo-figma-background-bg-04', '--yovo-figma-neutrals-grey-300'],
  ['--yovo-figma-divider-divider-01', '--yovo-figma-neutrals-grey-100'],
  ['--yovo-figma-divider-divider-02', '--yovo-figma-neutrals-grey-200'],
  ['--yovo-figma-divider-divider-03', '--yovo-figma-neutrals-grey-300'],
  ['--yovo-figma-divider-divider-04', '--yovo-figma-neutrals-grey-400'],
];

const sharedSpaceSteps = ['none', 'xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', '4xl', '5xl'];

/** Text Strings aliases the Space variables, including the steps Space itself does not publish. */
export const aliasSpaceLinks = [
  ...sharedSpaceSteps.map((step) => [
    `--yovo-figma-text-strings-${step}`,
    `--yovo-figma-space-${step}`,
  ]),
  ['--yovo-figma-text-strings-small-gaps', '--yovo-figma-space-small-gaps'],
  ['--yovo-figma-text-strings-gaps', '--yovo-figma-space-gaps'],
  ['--yovo-figma-text-strings-large-gaps', '--yovo-figma-space-large-gaps'],
  ['--yovo-figma-text-strings-curve', '--yovo-figma-space-curve'],
];

/** Mapped names point at alias variables, so a component token follows alias back to brand. */
export const mappedColorLinks = aliasColorLinks.map(([aliasName]) => [
  aliasName.replace('--yovo-figma-', '--yovo-figma-mapped-'),
  aliasName,
]);

export const mappedSpaceLinks = aliasSpaceLinks.map(([aliasName]) => [
  aliasName.replace('--yovo-figma-text-strings-', '--yovo-figma-mapped-space-'),
  aliasName,
]);

function linkedVariables(links) {
  return links.reduce((variables, [name, target]) => {
    variables[name] = `var(${target})`;
    return variables;
  }, {});
}

export const aliasColorVariables = linkedVariables(aliasColorLinks);
export const aliasSpaceVariables = linkedVariables(aliasSpaceLinks);
export const mappedColorVariables = linkedVariables(mappedColorLinks);
export const mappedSpaceVariables = linkedVariables(mappedSpaceLinks);

function typeVariables() {
  const variables = {
    '--yovo-figma-font-family': fontFamily.primary,
  };
  phoneTypeSpecs.forEach((spec) => {
    const name = spec.key.replace('/', '-');
    variables[`--yovo-figma-type-${name}-size`] = `${spec.size}px`;
    variables[`--yovo-figma-type-${name}-line-height`] = `${spec.lineHeight}px`;
    variables[`--yovo-figma-type-${name}-weight`] = String(spec.weight);
  });
  return variables;
}

function declarationBlock(variables) {
  return Object.entries(variables)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n');
}

export function figmaTokenCss() {
  const variables = {
    ...brandColorVariables,
    ...brandSpaceVariables,
    ...typeVariables(),
    ...aliasColorVariables,
    ...aliasSpaceVariables,
    ...mappedColorVariables,
    ...mappedSpaceVariables,
  };
  return `:root {\n${declarationBlock(variables)}\n}`;
}
